/**
 * GlobalAppLayout component
 *
 * This is the single AppLayout instance for the entire application.
 * It consumes the LayoutContext to dynamically configure navigation,
 * breadcrumbs, tools, and other layout properties based on the current route.
 *
 * Data-driven approach:
 * - Navigation is rendered based on `navigationType` config
 * - Breadcrumbs are rendered from data array
 * - Sticky headers and flashbars are managed through config
 */

import { AppLayout, AppLayoutProps, Flashbar, FlashbarProps } from '@amzn/awsui-components-core';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLayoutConfig } from '../../contexts/LayoutContext';
import { useFlashbar } from '../../hooks/useFlashbar';
import { NavigationType } from '../../types/layout/layout.types';
import { Breadcrumbs } from '../common/Breadcrumb';
import HomePageSideNavigation from '../common/HomePageSideNavigation';
import ProductLineRpnFpnSideNavigationV2 from '../manage-programs/ProductLineRpnFpnSideNavigationV2';
import { usePrefetchData } from 'src/hooks/usePrefetchData';

const DEFAULT_SPLIT_PANEL_SIZE: number = 750;

/**
 * Navigation component registry
 * Maps navigation type to component
 */
const NavigationComponents: Record<NavigationType, React.FC | null> = {
  home: HomePageSideNavigation,
  'manage-programs': ProductLineRpnFpnSideNavigationV2,
  none: null,
};

/**
 * Props for GlobalAppLayout component
 */
interface GlobalAppLayoutProps {
  children: React.ReactNode;
}

/**
 * GlobalAppLayout component
 *
 * Wraps all application routes with a single AppLayout instance.
 * Layout configuration is controlled by child components via LayoutContext.
 *
 * @example
 * ```tsx
 * <LayoutProvider>
 *   <GlobalAppLayout>
 *     <AppRoutes />
 *   </GlobalAppLayout>
 * </LayoutProvider>
 * ```
 */
export const GlobalAppLayout: React.FC<GlobalAppLayoutProps> = ({ children }) => {
  const { config, setConfig, setNavigationOpen, navigationOpen: contextNavigationOpen } = useLayoutConfig();
  const { pathname } = useLocation();
  const { flashbarItems: globalFlashbarItems } = useFlashbar();
  const [navigationOpen, setNavigationOpenLocal] = useState(true);

  // Sync local navigation state when context changes (e.g., from child components)
  useEffect(() => {
    setNavigationOpenLocal(contextNavigationOpen);
  }, [contextNavigationOpen]);

  // Handle navigation drawer toggle - sync with both local and context state
  const handleNavigationChange = useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      setNavigationOpenLocal(detail.open);
      setNavigationOpen(detail.open);
    },
    [setNavigationOpen]
  );

  // Prefetch expensive data at app startup (fire-and-forget)
  usePrefetchData();

  const [toolsOpen, setToolsOpen] = useState(config.tools.open ?? false);
  const [splitPanelOpen, setSplitPanelOpen] = useState(config.splitPanel.open ?? false);
  const [splitPanelPreferences, setSplitPanelPreferences] = useState<AppLayoutProps.SplitPanelPreferences>(
    config.splitPanel.preferences ?? { position: 'side' }
  );
  const [splitPanelSize, setSplitPanelSize] = useState<number>(config.splitPanel.size ?? DEFAULT_SPLIT_PANEL_SIZE);

  // Close split panel and tools panel when user switches pages
  useEffect(() => {
    setSplitPanelOpen(false);
    setToolsOpen(false);
    setConfig({ tools: { open: false, hide: true } });
  }, [pathname, setConfig]);

  // Sync tools open state with config
  useEffect(() => {
    if (config.tools.open !== undefined) {
      setToolsOpen(config.tools.open);
    }
  }, [config.tools.open]);

  useEffect(() => {
    if (config.splitPanel.preferences) {
      setSplitPanelPreferences(config.splitPanel.preferences);
    }
  }, [config.splitPanel.preferences]);

  useEffect(() => {
    if (config.splitPanel.size) {
      setSplitPanelSize(config.splitPanel.size);
    }
  }, [config.splitPanel.size]);

  useEffect(() => {
    if (config.splitPanel.open !== undefined) {
      setSplitPanelOpen(config.splitPanel.open);
    }
  }, [config.splitPanel.open]);

  /**
   * Handle tools panel change - sync with config callback if provided
   */
  const handleToolsChange = useCallback(
    ({ detail }: { detail: { open: boolean } }) => {
      setToolsOpen(detail.open);
      setConfig({ tools: { open: detail.open, hide: !detail.open } });
      if (config.tools.onChange) {
        config.tools.onChange({ detail });
      }
    },
    [config.tools, setConfig]
  );

  /**
   * Render navigation component based on config type
   */
  const navigation = useMemo(() => {
    const NavigationComponent = NavigationComponents[config.navigationType];
    if (!NavigationComponent) return undefined;
    return <NavigationComponent />;
  }, [config.navigationType]);

  /**
   * Render breadcrumbs from data array
   */
  const breadcrumbs = useMemo(() => {
    // If sticky header is enabled, breadcrumbs are rendered in notifications area
    if (config.stickyHeader.enabled) return undefined;

    return (
      <Breadcrumbs
        items={config.breadcrumbs.map((item) => ({
          text: item.text,
          href: item.href || '',
        }))}
      />
    );
  }, [config.breadcrumbs, config.stickyHeader.enabled]);

  /**
   * Combine global flashbar items with page-specific items and render notifications
   */
  const notifications = useMemo(() => {
    // Combine page flashbar items with global flashbar items
    const allFlashbarItems: FlashbarProps.MessageDefinition[] = [
      ...config.pageFlashbarItems.map((item) => ({
        id: item.id,
        type: item.type === 'in-progress' ? ('info' as const) : item.type,
        loading: item.type === 'in-progress',
        content: item.content,
        dismissible: item.dismissible,
        dismissLabel: item.dismissLabel,
        onDismiss: item.onDismiss,
      })),
      ...globalFlashbarItems,
    ];

    // If sticky header is enabled, include breadcrumbs and header in notifications
    if (config.stickyHeader.enabled) {
      const stickyBreadcrumbs = config.stickyHeader.breadcrumbs || config.breadcrumbs;
      return (
        <>
          <Breadcrumbs
            items={stickyBreadcrumbs.map((item) => ({
              text: item.text,
              href: item.href || '',
            }))}
          />
          {config.stickyHeader.component}
          {allFlashbarItems.length > 0 && <Flashbar items={allFlashbarItems} stackItems />}
        </>
      );
    }

    // Normal notifications - just flashbar
    if (allFlashbarItems.length === 0) return undefined;
    return <Flashbar items={allFlashbarItems} stackItems />;
  }, [config.stickyHeader, config.breadcrumbs, config.pageFlashbarItems, globalFlashbarItems]);

  // Track sticky notifications height using ResizeObserver
  // Sets CSS custom property --sticky-notifications-height for child components
  const stickyNotificationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!config.stickyHeader.enabled || !stickyNotificationsRef.current) {
      // Reset the CSS variable when sticky header is disabled
      document.documentElement.style.removeProperty('--sticky-notifications-height');
      return;
    }

    const updateHeight = () => {
      if (stickyNotificationsRef.current) {
        const height = stickyNotificationsRef.current.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--sticky-notifications-height', `${height}px`);
      }
    };

    // Initial measurement
    updateHeight();

    // Observe for size changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(stickyNotificationsRef.current);

    return () => {
      resizeObserver.disconnect();
      document.documentElement.style.removeProperty('--sticky-notifications-height');
    };
  }, [config.stickyHeader.enabled]);

  // Wrap notifications in a div with ref when sticky header is enabled
  const wrappedNotifications = useMemo(() => {
    if (!notifications) return undefined;

    if (config.stickyHeader.enabled) {
      return <div ref={stickyNotificationsRef}>{notifications}</div>;
    }

    return notifications;
  }, [notifications, config.stickyHeader.enabled]);

  return (
    <AppLayout
      headerSelector="#h"
      navigation={navigation}
      navigationOpen={navigationOpen}
      navigationHide={config.navigationHide}
      onNavigationChange={handleNavigationChange}
      breadcrumbs={breadcrumbs}
      tools={config.tools.component}
      toolsHide={config.tools.hide}
      toolsOpen={toolsOpen}
      toolsWidth={config.tools.width}
      onToolsChange={handleToolsChange}
      splitPanel={config.splitPanel.component}
      splitPanelOpen={splitPanelOpen}
      splitPanelSize={splitPanelSize}
      onSplitPanelResize={({ detail }) => setSplitPanelSize(detail.size)}
      onSplitPanelToggle={({ detail }) => setConfig({ splitPanel: { open: detail.open } })}
      splitPanelPreferences={splitPanelPreferences}
      onSplitPanelPreferencesChange={({ detail }) => setSplitPanelPreferences(detail)}
      contentType={config.contentType}
      maxContentWidth={config.maxContentWidth}
      notifications={wrappedNotifications}
      stickyNotifications={config.stickyHeader.enabled}
      content={children}
    />
  );
};
